import LeaseApiService from './apiService';
import { endPoint } from '../EndPoints/endPoint';

/**
 * @typedef {{ value: string, text: string }} DropdownItem
 * @typedef {{ isCC1Required: boolean, isCC2Required: boolean, isCC3Required: boolean, isCC4Required: boolean,
 *   labels: { costCenter1: string|null, costCenter2: string|null, costCenter3: string|null, costCenter4: string|null } }} Settings
 * @typedef {{ taxCode: string, description: string, rate: number, salesTaxRate: number, vatAcCode: string, stAcCode: string, fromDate: string|null }} TaxCode
 * @typedef {{ code: string, conversionRate: number, isDefault: boolean }} Currency
 * @typedef {{ customerNo: string, customerName: string }} PartyCustomer
 * @typedef {{ customerNo: string, customerName: string, address1: string, address2: string, cityCountry: string,
 *   contactNo: string, vatRegNo: string, taxCode: string|null, balance: number }} PartyDetail
 * @typedef {{ voucherNo: string, financialYear: string, postingId: string, voucherSerialNo: number, postingType: string,
 *   postingDate: string, voucherType: string, transactionMethod: string, customerNo: string, customerName: string,
 *   refNo: string, invoiceNo: string, invoiceDate: string, dueDate: string, orderNo: string, currency: string,
 *   conversionRate: number, amount: number, convertedAmount: number, narration: string, costCenter1: string,
 *   costCenter2: string, costCenter3: string, costCenter4: string, batchNo: string, receiptNo: string,
 *   isApproved: boolean, canEdit: boolean, amountReceived: number, createdBy: string, createdOn: string,
 *   totalRecords: number }} VoucherRow
 * @typedef {{ acCode: string, acName: string, percentage: number, credit: number, taxCode: string, vat: number, st: number,
 *   total: number, conRate: number, convertedAmount: number, narration: string, costCenter1: string, costCenter2: string,
 *   costCenter3: string, costCenter4: string, vatAcCode: string, stAcCode: string, enteredAmount: number }} VoucherLine
 * @typedef {VoucherRow & { orderDate: string, taxCode: string, taxInclusive: boolean, editBlockReason: string|null }} VoucherHeader
 * @typedef {{ header: VoucherHeader, lines: VoucherLine[] }} VoucherDetail
 * @typedef {{ acCode: string, enteredAmount: number, taxCode: string, percentage: number, narration: string,
 *   costCenter1: string, costCenter2: string, costCenter3: string, costCenter4: string }} SaveLine
 * @typedef {{ postingDate: string, voucherType: string, customerNo: string, refNo: string|null, invoiceNo: string|null,
 *   invoiceDate: string|null, dueDate: string|null, orderNo: string|null, orderDate: string|null, currency: string,
 *   conversionRate: number, amount: number, narration: string, taxCode: string, taxInclusive: boolean,
 *   costCenter1: string, costCenter2: string, costCenter3: string, costCenter4: string, copyCostCentres: boolean,
 *   batchNo: string|null, financialYear?: string, lines: SaveLine[] }} SaveRequest
 * @typedef {{ voucherNo: string, postingId: string, voucherSerialNo: number, postingType: string, receiptNo: string,
 *   financialYear: string }} SaveResult
 * @typedef {{ ok: boolean, status: number, message: string, result: any, items: any, dropdowns: any, pagination: any }} ApiResult
 */

const api = new LeaseApiService();
const http = api.axiosInstance;

const GENERIC_ERROR = 'Something went wrong. Please try again.';
export const NOT_FOUND_MESSAGE = 'Voucher not found.';
// Posting can take a while; only create and update get the long timeout.
export const SAVE_TIMEOUT_MS = 20 * 60 * 1000;

/** 400 carries a readable message; 404 and 409 are handled by status; anything else is generic. */
const errorMessage = (status, serverMessage) => {
    if (status === 400) return serverMessage || 'Invalid request.';
    if (status === 404) return serverMessage || NOT_FOUND_MESSAGE;
    if (status === 409) return serverMessage || 'This voucher has been approved and can no longer be edited.';
    return GENERIC_ERROR;
};

/** Normalises BaseResponse / PaginationResponse / DropdownResponse so callers never touch axios errors. */
const call = async (request) => {
    try {
        const res = await request();
        const body = res?.data;
        const status = res?.status === 204 ? 204 : (body?.statusCode ?? res?.status);
        const ok = status >= 200 && status < 300;
        return {
            ok,
            status,
            message: ok ? body?.message || '' : errorMessage(status, body?.message),
            result: body?.result ?? body?.data ?? null,
            items: body?.items ?? null,
            dropdowns: body?.dropdowns ?? null,
            pagination: body?.paginationData ?? null,
        };
    } catch (error) {
        const status = error?.response?.status || 0;
        return { ok: false, status, message: errorMessage(status, error?.response?.data?.message), result: null, items: null, dropdowns: null, pagination: null };
    }
};

/** Leaves out empty filters. */
const withQuery = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && `${value}`.trim() !== '') q.append(key, `${value}`.trim());
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

// Lookups are loaded once and kept for the session; a failed load is not cached.
const cache = new Map();
const cached = (key, request) => {
    if (!cache.has(key)) {
        const pending = request().then((res) => {
            if (!res.ok && res.status !== 204) cache.delete(key);
            return res;
        });
        cache.set(key, pending);
    }
    return cache.get(key);
};

/** @param {string[]} dropdownTypes */
export const getDropdowns = (dropdownTypes) => {
    const key = `dd:${[...dropdownTypes].sort().join(',')}`;
    return cached(key, () => call(() => http.post(endPoint.SVRA_DROPDOWNS, { dropdownTypes })));
};

export const getSettings = () => cached('settings', () => call(() => http.get(endPoint.SVRA_SETTINGS)));

export const getTaxCodes = () => cached('taxCodes', () => call(() => http.get(endPoint.SVRA_TAX_CODES)));

export const getCurrencies = () => cached('currencies', () => call(() => http.get(endPoint.SVRA_CURRENCIES)));

/** level 1-4; each level needs the levels above it. */
export const getCostCentres = ({ level, costCenter1, costCenter2, costCenter3 }) => {
    const query = withQuery({ level, costCenter1, costCenter2, costCenter3 });
    return cached(`cc:${query}`, () => call(() => http.get(endPoint.SVRA_COST_CENTRES + query)));
};

/** Resolves to a plain [{ value, text }] list ([] on failure), for the cascading selects. */
export const getCostCentreList = async (params) => {
    const res = await getCostCentres(params);
    return res.ok ? (res.items || res.result || []) : [];
};

/** partyType: All | Tenant | Landlord | Freehold. useCoa lists ledger accounts instead of customers. */
export const getPartyCustomers = ({ partyType = 'All', useCoa = false, costCenter1, costCenter2 }) =>
    call(() => http.get(endPoint.SVRA_PARTY_CUSTOMERS + withQuery({ partyType, useCoa, costCenter1, costCenter2 })));

export const getCustomerDetail = ({ customerNo, financialYear }) =>
    call(() => http.get(endPoint.SVRA_CUSTOMER_DETAIL + withQuery({ customerNo, financialYear })));

/** @param {object} filters */
export const getVouchers = (filters = {}) => call(() => http.get(endPoint.SVRA_LIST + withQuery(filters)));

/** financialYear is part of the key: take it from the listing row. */
export const getVoucherByNo = (voucherNo, financialYear) =>
    call(() => http.get(`${endPoint.SVRA_BY_NO}${encodeURIComponent(voucherNo)}${withQuery({ financialYear })}`));

/** @param {SaveRequest} body */
export const createVoucher = (body) => call(() => http.post(endPoint.SVRA_CREATE, body, { timeout: SAVE_TIMEOUT_MS }));

/** @param {SaveRequest} body voucherNo travels in the route, never in the body. */
export const updateVoucher = (voucherNo, body) =>
    call(() => http.put(`${endPoint.SVRA_UPDATE}${encodeURIComponent(voucherNo)}`, body, { timeout: SAVE_TIMEOUT_MS }));

const saleVoucherRAApi = {
    getDropdowns, getSettings, getTaxCodes, getCurrencies, getCostCentres, getCostCentreList, getPartyCustomers,
    getCustomerDetail, getVouchers, getVoucherByNo, createVoucher, updateVoucher,
};

export default saleVoucherRAApi;
